import React, { useEffect, useState, createContext, useContext, ReactNode, useCallback, useMemo, useRef } from "react";
import type { HumanBehaviorTracker } from "humanbehavior-js";

/**
 * The React surface, with the recorder held at arm's length.
 *
 * Everything here used to import the bundled tracker directly, which meant any
 * app importing the provider also shipped rrweb and the whole recorder in its
 * own build - and could only get SDK fixes by reinstalling. The logic is
 * unchanged; it now receives whatever provides `init`, so the same provider can
 * be bound to the bundled recorder (`humanbehavior-js/react`) or to the
 * auto-updating loader (`humanbehavior-js/react/auto`).
 *
 * The tracker type is imported for types only, so it is erased at build time and
 * never pulls the recorder into a bundle.
 */

/** Anything that can produce a tracker: the bundled class, or the CDN loader stub. */
export interface TrackerHost {
  init(apiKey: string, options: Record<string, any>): any;
}

export interface FactoryLogger {
  error: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  debug: (...args: any[]) => void;
}

// Check if we're in a browser environment
const isBrowser = () => typeof window !== 'undefined';

// `identityToken` is minted by the app's own backend for a user it has already
// authenticated. Projects that require identity verification reject an identify
// that claims an externalUserId without one.
export interface IdentifyUserArgs {
  userProperties: Record<string, any>;
  identityToken?: string;
}

// Define the public interface that components will interact with
export interface HumanBehaviorInterface {
  addEvent: (event: any) => void;
  identifyUser: (args: IdentifyUserArgs) => Promise<string>;
  start: () => void;
  stop: () => void;
  logout: () => void;
  viewLogs: () => void;
  setUnredactedFields: (fields: string[]) => void;
  hasUnredactedFields: () => boolean;
  getUnredactedFields: () => string[];
  trackPageView: () => void;
}

export interface HumanBehaviorProviderProps {
  // Either provide an apiKey to create a new client, or provide an existing client
  apiKey?: string;
  client?: HumanBehaviorTracker;
  children: ReactNode;
  options?: {
    ingestionUrl?: string;
    logLevel?: 'none' | 'error' | 'warn' | 'info' | 'debug';
    redactFields?: string[]; // DEPRECATED: Use redactionStrategy instead
    redactionStrategy?: {
      mode: 'privacy-first' | 'visibility-first'; // Default: 'privacy-first'
      unredactFields?: string[]; // Fields to make visible (when mode: 'privacy-first')
      redactFields?: string[];   // Fields to hide (when mode: 'visibility-first')
    };
    enableAutomaticTracking?: boolean;
    suppressConsoleErrors?: boolean;
    recordCanvas?: boolean; // Enable canvas recording with protection
    enableAutomaticProperties?: boolean; // Enable automatic property detection
    propertyDenylist?: string[]; // Properties to exclude from tracking
    automaticTrackingOptions?: {
      trackButtons?: boolean;
      trackLinks?: boolean;
      trackForms?: boolean;
      includeText?: boolean;
      includeClasses?: boolean;
    };
    maxQueueSize?: number; // Configurable queue size
    enableConsoleTracking?: boolean; // Enable console warn/error tracking (default: true, set to false to opt-out)
    enableNetworkTracking?: boolean; // Enable network error tracking (default: true, set to false to opt-out)
    enableErrorTracking?: boolean; // Enable crash/error capture (default: true, set to false to opt-out)
    enableWebVitals?: boolean; // Enable Core Web Vitals tracking (FCP/LCP/CLS/INP/TTFB) (default: true, set to false to opt-out)
    enableTracing?: boolean; // Enable distributed tracing (page-load/resource/custom spans) (default: true, set to false to opt-out)
    release?: string; // App release/version stamped on each error
    environment?: string; // Deployment environment stamped on each error
    commitSha?: string; // Git commit SHA stamped on each error report (enables GitHub blob links)
    dist?: string; // Build/dist discriminator stamped on each error report (pairs with release for source-map lookup)
    captureRequestBodies?: boolean; // Opt-in: attach redacted request/response bodies + headers to error-correlated requests (default: false)
    /** Auto-updating installs only: serve the loader from your own origin. */
    cdnUrl?: string;
    /** Auto-updating installs only: pin an exact recorder build. */
    version?: string;
  };
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  client?: HumanBehaviorTracker;
  onError?: (error: Error, componentStack: string | null) => void;
}

export interface ErrorBoundaryState {
  hasError: boolean;
}

export function createHumanBehaviorReact(host: TrackerHost, log: FactoryLogger) {
  const HumanBehaviorContext = createContext<HumanBehaviorTracker | HumanBehaviorInterface | null>(null);

  // Memoized queuing implementation for initialization period
  const createQueuingImplementation = (queueEvent: (event: any) => void): HumanBehaviorInterface => ({
    addEvent: (event: any) => {
      queueEvent(event);
    },
    identifyUser: async ({ userProperties, identityToken }: IdentifyUserArgs) => {
      queueEvent({
        type: 'identify',
        userProperties,
        identityToken,
      });
      return ''; // Return empty string to match interface
    },
    start: () => {
      // Start will be called automatically when initialized
    },
    stop: () => {
      // Stop is a no-op when not initialized
    },
    logout: () => {
      // Logout is a no-op when not initialized
    },
    viewLogs: () => {
      log.warn('Logs are not available until HumanBehaviorTracker is initialized');
    },
    setUnredactedFields: () => {},
    hasUnredactedFields: () => false,
    getUnredactedFields: () => [],
    trackPageView: () => {},
  });

  // Automatic page tracking component
  function HumanBehaviorPageView() {
    const tracker = useContext(HumanBehaviorContext);

    useEffect(() => {
      if (tracker && typeof window !== 'undefined') {
        // Track initial page load
        tracker.trackPageView();

        // Listen for route changes (for SPAs)
        const handleRouteChange = () => {
          tracker.trackPageView();
        };

        // Listen for popstate (back/forward navigation)
        window.addEventListener('popstate', handleRouteChange);

        // Listen for pushstate/replacestate (programmatic navigation)
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;

        history.pushState = function(...args) {
          originalPushState.apply(this, args);
          handleRouteChange();
        };

        history.replaceState = function(...args) {
          originalReplaceState.apply(this, args);
          handleRouteChange();
        };

        return () => {
          window.removeEventListener('popstate', handleRouteChange);
          history.pushState = originalPushState;
          history.replaceState = originalReplaceState;
        };
      }
    }, [tracker]);

    return null;
  }

  const HumanBehaviorProvider = ({ apiKey, client, children, options }: HumanBehaviorProviderProps) => {
    const [humanBehavior, setHumanBehavior] = useState<HumanBehaviorTracker | null>(client || null);
    const [eventQueue, setEventQueue] = useState<any[]>([]);
    const [isMounted, setIsMounted] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Use refs to avoid dependency issues in useEffect
    const apiKeyRef = useRef(apiKey);
    const clientRef = useRef(client);
    const eventQueueRef = useRef(eventQueue);

    // Update refs when props change
    useEffect(() => {
      apiKeyRef.current = apiKey;
      clientRef.current = client;
    }, [apiKey, client]);

    // Update eventQueue ref when queue changes
    useEffect(() => {
      eventQueueRef.current = eventQueue;
    }, [eventQueue]);

    // Memoized queueEvent function to prevent unnecessary re-renders
    const queueEvent = useCallback((event: any) => {
      setEventQueue(prev => [...prev, event]);
    }, []);

    // Handle mounting state
    useEffect(() => {
      setIsMounted(true);
    }, []);

    useEffect(() => {
      // Only run in browser
      if (!(isBrowser())) {
        return;
      }

      // If client is provided, use that
      if (clientRef.current) {
        setHumanBehavior(clientRef.current);
        setIsInitialized(true);
        return;
      }

      // If no client is provided, apiKey is required
      if (!apiKeyRef.current || apiKeyRef.current.trim() === '') {
        log.error("An apiKey is required when no client is provided");
        return;
      }

      // Prevent multiple initializations
      if (humanBehavior !== null) {
        return;
      }

      try {

        // Create new tracker instance with the validated apiKey and options
        const tracker = host.init(
          apiKeyRef.current.trim(),
          {
            ingestionUrl: options?.ingestionUrl,
            logLevel: options?.logLevel,
            redactFields: options?.redactFields, // Legacy support
            redactionStrategy: options?.redactionStrategy, // New redaction strategy
            enableAutomaticTracking: options?.enableAutomaticTracking,
            suppressConsoleErrors: options?.suppressConsoleErrors,
            recordCanvas: options?.recordCanvas,
            enableAutomaticProperties: options?.enableAutomaticProperties,
            propertyDenylist: options?.propertyDenylist,
            automaticTrackingOptions: options?.automaticTrackingOptions,
            maxQueueSize: options?.maxQueueSize,
            enableConsoleTracking: options?.enableConsoleTracking,
            enableNetworkTracking: options?.enableNetworkTracking,
            enableErrorTracking: options?.enableErrorTracking,
            enableWebVitals: options?.enableWebVitals,
            enableTracing: options?.enableTracing,
            release: options?.release,
            environment: options?.environment,
            commitSha: options?.commitSha,
            dist: options?.dist,
            captureRequestBodies: options?.captureRequestBodies,
            cdnUrl: options?.cdnUrl,
            version: options?.version,
          }
        );

        setHumanBehavior(tracker);

        // Mark as initialized immediately (don't wait for server init)
        setIsInitialized(true);

        // Start tracking in background
        tracker.initializationPromise?.then(async () => {
            // Only start if not already started
            if (!tracker.isTrackerStarted) {
                await tracker.start();
            }

          // Process any queued events
          const currentQueue = eventQueueRef.current;
          if (currentQueue.length > 0) {
            for (const event of currentQueue) {
              if (event.type === 'identify') {
                log.debug('Processing queued identify event', event);
                try {
                  await tracker.identifyUser({
                    userProperties: event.userProperties,
                    identityToken: event.identityToken,
                  });
                } catch (error) {
                  log.error('Failed to process queued user info:', error);
                }
              } else {
                tracker.addEvent(event);
              }
            }
            setEventQueue([]); // Clear the queue
          }
        }).catch((error: unknown) => {
          log.error('Failed to initialize HumanBehaviorTracker:', error);
        });
      } catch (error) {
        log.error('Failed to create HumanBehaviorTracker:', error);
      }
    }, [isMounted]); // Removed humanBehavior from dependencies to prevent infinite loop

    // Memoized context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => {
      if (!isMounted) {
        return createQueuingImplementation(queueEvent);
      }

      if (!isInitialized) {
        return createQueuingImplementation(queueEvent);
      }

      return humanBehavior;
    }, [isMounted, isInitialized, humanBehavior, queueEvent]);

    // If not in browser, render children without context
    if (!(isBrowser())) {
      return <>{children}</>;
    }

    return (
      <HumanBehaviorContext.Provider value={contextValue}>
        <HumanBehaviorPageView />
        {children}
      </HumanBehaviorContext.Provider>
    );
  };

  const useHumanBehavior = () => {
    const tracker = useContext(HumanBehaviorContext);
    if (!tracker) {
      throw new Error('useHumanBehavior must be used within a HumanBehaviorProvider');
    }
    return tracker;
  };

  // Custom hook for managing unredaction fields dynamically
  const useRedaction = () => {
    const tracker = useHumanBehavior();

    const setUnredactedFields = useCallback((fields: string[]) => {
      tracker.setUnredactedFields(fields);
    }, [tracker]);

    const hasUnredactedFields = useCallback(() => {
      return tracker.hasUnredactedFields();
    }, [tracker]);

    const getUnredactedFields = useCallback(() => {
      return tracker.getUnredactedFields();
    }, [tracker]);

    return {
      setUnredactedFields,
      hasUnredactedFields,
      getUnredactedFields
    };
  };

  // Custom hook for managing user info
  const useUserTracking = () => {
    const tracker = useHumanBehavior();

    const identifyUser = useCallback(async ({ userProperties, identityToken }: IdentifyUserArgs) => {
      try {
        await tracker.identifyUser({ userProperties, identityToken });
        return { success: true };
      } catch (error) {
        log.error('Failed to identify user:', error);
        return { success: false, error };
      }
    }, [tracker]);

    return {
      identifyUser
    };
  };

  // Error boundary that reports React render crashes (with the component stack)
  // to HumanBehavior, then renders an optional fallback. Wrap any subtree:
  //   <HumanBehaviorErrorBoundary fallback={<Oops />}> ... </HumanBehaviorErrorBoundary>
  // It reads the tracker from context (or an explicit `client` prop) and calls
  // `tracker.captureException(error, { componentStack, mechanism: 'react' })`.
  class HumanBehaviorErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    static contextType = HumanBehaviorContext;
    declare context: React.ContextType<typeof HumanBehaviorContext>;

    constructor(props: ErrorBoundaryProps) {
      super(props);
      this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
      return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
      const componentStack = info?.componentStack ?? null;
      try {
        const tracker = this.props.client ?? this.context;
        if (tracker && typeof (tracker as HumanBehaviorTracker).captureException === 'function') {
          (tracker as HumanBehaviorTracker).captureException(error, {
            componentStack: componentStack ?? undefined,
            mechanism: 'react',
          });
        }
      } catch {
        // Reporting must never break the host app.
      }
      try {
        this.props.onError?.(error, componentStack);
      } catch {
        // Ignore consumer callback errors.
      }
    }

    render() {
      if (this.state.hasError) {
        return this.props.fallback ?? null;
      }
      return this.props.children;
    }
  }

  return {
    HumanBehaviorContext,
    HumanBehaviorProvider,
    useHumanBehavior,
    useRedaction,
    useUserTracking,
    // Annotated, not inferred. A class created inside a function infers to a
    // structural constructor type that has lost its relationship to
    // React.Component, and TypeScript then rejects `<HumanBehaviorErrorBoundary>`
    // with TS2786 in the consumer's build.
    HumanBehaviorErrorBoundary:
      HumanBehaviorErrorBoundary as React.ComponentClass<ErrorBoundaryProps, ErrorBoundaryState>,
  };
}

// Next.js App Router route-error helper.
//
// Next renders `app/error.tsx` / `app/global-error.tsx` (client components) when
// a route segment throws on the client. These boundaries are frequently mounted
// OUTSIDE the HumanBehaviorProvider tree (global-error.tsx replaces the root
// layout), so this resolves the tracker from the global singleton (or an
// explicit `client`) rather than React context. Use it from the boundary:
//
//   'use client';
//   import { captureNextError } from 'humanbehavior-js/react';
//   export default function Error({ error, reset }) {
//     useEffect(() => { captureNextError(error); }, [error]);
//     return <button onClick={reset}>Try again</button>;
//   }
//
// Limitation: captures CLIENT-side route errors only. Server Component / RSC
// errors that never reach the browser require a Next server module
// (instrumentation.ts `onRequestError`) and are out of scope for this browser SDK.
//
// On an auto-updating install the global singleton is published by the recorder
// once it arrives, so an error thrown in the first few hundred milliseconds of a
// page load is not reported.
export function captureNextError(
  error: unknown,
  options?: { client?: HumanBehaviorTracker; componentStack?: string }
): void {
  try {
    const tracker =
      options?.client ??
      (typeof window !== 'undefined'
        ? ((window as unknown as { __humanBehaviorGlobalTracker?: HumanBehaviorTracker }).__humanBehaviorGlobalTracker)
        : undefined);
    if (tracker && typeof tracker.captureException === 'function') {
      tracker.captureException(error, {
        componentStack: options?.componentStack,
        mechanism: 'react',
      });
    }
  } catch {
    // Reporting must never break the host app.
  }
}
